import React, { useMemo, useState, useEffect } from 'react';
import { Card, Row, Col, Typography, DatePicker, Input, Space, Select } from 'antd';
import dayjs from 'dayjs';
import type { ColumnsType } from 'antd/es/table';
import StandardTable from '../../../components/common/StandardTable';
import { useGetMatClntDeliveries } from '../../../hooks/genericHooks';
import type { MatClntDelivery } from '../../../types';
import { formatNumberSmart } from '../../../utils/numberFormat';
import Button from '../../../components/common/Button';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

export default function MatClntDeliveries() {
  const { data = [], isLoading } = useGetMatClntDeliveries() as any;
  const deliveries: MatClntDelivery[] = Array.isArray(data) ? data : (data?.data ?? []);
  

  // Local UI filters
  const [search, setSearch] = useState<string>('');
  const [dateRange, setDateRange] = useState<[string | null, string | null]>([null, null]);
  const [shipmentSel, setShipmentSel] = useState<string>('');
  const [unitSel, setUnitSel] = useState<string>('');
  const [groupSel, setGroupSel] = useState<string>('');
  const [sizeSel, setSizeSel] = useState<string>('');
  const [statusSel, setStatusSel] = useState<string>('');

  const norm = (s: any) => (s == null ? '' : String(s)).trim();

  // Cascaded options
  const shipmentOptions = useMemo(() => {
    const byKey = new Map<string, string>();
    const s = (search || '').trim().toLowerCase();
    const phraseMatches = Array.from(s.matchAll(/"([^\"]+)"/g)).map(m => m[1].trim()).filter(Boolean);
    const remaining = s.replace(/"[^\"]+"/g, ' ').trim();
    const tokens = remaining.split(/\s+/).filter(Boolean);
    (deliveries || [])
      .filter(rec => {
        if (!s) return true;
        const hay = [
          String(rec.mirr_no || ''),
          String(rec.invoice_no || ''),
          String(rec.package_no || ''),
          String(rec.ident_code || ''),
          rec.short_desc || '',
          rec.long_desc || '',
          rec.delivery_location || '',
        ].join(' ').toLowerCase();
        if (phraseMatches.length > 0 && !phraseMatches.every(p => hay.includes(p))) return false;
        if (tokens.length > 0 && !tokens.every(t => hay.includes(t))) return false;
        return true;
      })
      .filter(r => !unitSel || norm(r.unit).toLowerCase() === norm(unitSel).toLowerCase())
      .filter(r => !groupSel || norm(r.material_group).toLowerCase() === norm(groupSel).toLowerCase())
      .filter(r => !statusSel || norm(r.delivery_status).toLowerCase() === norm(statusSel).toLowerCase())
      .filter(r => !sizeSel || norm(r.size_text).toLowerCase() === norm(sizeSel).toLowerCase())
      .forEach(r => {
        const raw = r?.shipment_id == null ? '' : String(r.shipment_id);
        if (!raw) return;
        const k = raw.toLowerCase();
        if (!byKey.has(k)) byKey.set(k, raw);
      });
    return Array.from(byKey.values()).sort((a,b)=>a.localeCompare(b)).map(v => ({ value: v, label: v }));
  }, [deliveries, unitSel, groupSel, statusSel, sizeSel, search]);

  const unitOptions = useMemo(() => {
    const byKey = new Map<string, string>();
    const s = (search || '').trim().toLowerCase();
    const phraseMatches = Array.from(s.matchAll(/"([^\"]+)"/g)).map(m => m[1].trim()).filter(Boolean);
    const remaining = s.replace(/"[^\"]+"/g, ' ').trim();
    const tokens = remaining.split(/\s+/).filter(Boolean);
    (deliveries || [])
      .filter(rec => {
        if (!s) return true;
        const hay = [
          String(rec.mirr_no || ''),
          String(rec.invoice_no || ''),
          String(rec.package_no || ''),
          String(rec.ident_code || ''),
          rec.short_desc || '',
          rec.long_desc || '',
          rec.delivery_location || '',
        ].join(' ').toLowerCase();
        if (phraseMatches.length > 0 && !phraseMatches.every(p => hay.includes(p))) return false;
        if (tokens.length > 0 && !tokens.every(t => hay.includes(t))) return false;
        return true;
      })
      .filter(r => !shipmentSel || String(r.shipment_id || '') === shipmentSel)
      .filter(r => !groupSel || norm(r.material_group).toLowerCase() === norm(groupSel).toLowerCase())
      .filter(r => !statusSel || norm(r.delivery_status).toLowerCase() === norm(statusSel).toLowerCase())
      .filter(r => !sizeSel || norm(r.size_text).toLowerCase() === norm(sizeSel).toLowerCase())
      .forEach(r => {
        const raw = norm(r.unit);
        if (!raw) return;
        const k = raw.toLowerCase();
        if (!byKey.has(k)) byKey.set(k, raw);
      });
    return Array.from(byKey.values()).sort((a,b)=>a.localeCompare(b)).map(v => ({ value: v, label: v }));
  }, [deliveries, shipmentSel, groupSel, statusSel, sizeSel, search]);

  const groupOptions = useMemo(() => {
    const byKey = new Map<string, string>();
    const s = (search || '').trim().toLowerCase();
    const phraseMatches = Array.from(s.matchAll(/"([^\"]+)"/g)).map(m => m[1].trim()).filter(Boolean);
    const remaining = s.replace(/"[^\"]+"/g, ' ').trim();
    const tokens = remaining.split(/\s+/).filter(Boolean);
    (deliveries || [])
      .filter(rec => {
        if (!s) return true;
        const hay = [
          String(rec.mirr_no || ''),
          String(rec.invoice_no || ''),
          String(rec.package_no || ''),
          String(rec.ident_code || ''),
          rec.short_desc || '',
          rec.long_desc || '',
          rec.delivery_location || '',
        ].join(' ').toLowerCase();
        if (phraseMatches.length > 0 && !phraseMatches.every(p => hay.includes(p))) return false;
        if (tokens.length > 0 && !tokens.every(t => hay.includes(t))) return false;
        return true;
      })
      .filter(r => !shipmentSel || String(r.shipment_id || '') === shipmentSel)
      .filter(r => !unitSel || norm(r.unit).toLowerCase() === norm(unitSel).toLowerCase())
      .filter(r => !statusSel || norm(r.delivery_status).toLowerCase() === norm(statusSel).toLowerCase())
      .filter(r => !sizeSel || norm(r.size_text).toLowerCase() === norm(sizeSel).toLowerCase())
      .forEach(r => {
        const raw = norm(r.material_group);
        if (!raw) return;
        const k = raw.toLowerCase();
        if (!byKey.has(k)) byKey.set(k, raw);
      });
    return Array.from(byKey.values()).sort((a,b)=>a.localeCompare(b)).map(v => ({ value: v, label: v }));
  }, [deliveries, shipmentSel, unitSel, statusSel, sizeSel, search]);

  const statusOptions = useMemo(() => {
    const byKey = new Map<string, string>();
    const s = (search || '').trim().toLowerCase();
    const phraseMatches = Array.from(s.matchAll(/"([^\"]+)"/g)).map(m => m[1].trim()).filter(Boolean);
    const remaining = s.replace(/"[^\"]+"/g, ' ').trim();
    const tokens = remaining.split(/\s+/).filter(Boolean);
    (deliveries || [])
      .filter(rec => {
        if (!s) return true;
        const hay = [
          String(rec.mirr_no || ''),
          String(rec.invoice_no || ''),
          String(rec.package_no || ''),
          String(rec.ident_code || ''),
          rec.short_desc || '',
          rec.long_desc || '',
          rec.delivery_location || '',
        ].join(' ').toLowerCase();
        if (phraseMatches.length > 0 && !phraseMatches.every(p => hay.includes(p))) return false;
        if (tokens.length > 0 && !tokens.every(t => hay.includes(t))) return false;
        return true;
      })
      .filter(r => !shipmentSel || String(r.shipment_id || '') === shipmentSel)
      .filter(r => !unitSel || norm(r.unit).toLowerCase() === norm(unitSel).toLowerCase())
      .filter(r => !groupSel || norm(r.material_group).toLowerCase() === norm(groupSel).toLowerCase())
      .filter(r => !sizeSel || norm(r.size_text).toLowerCase() === norm(sizeSel).toLowerCase())
      .forEach(r => {
        const raw = norm(r.delivery_status);
        if (!raw) return;
        const k = raw.toLowerCase();
        if (!byKey.has(k)) byKey.set(k, raw);
      });
    return Array.from(byKey.values()).sort((a,b)=>a.localeCompare(b)).map(v => ({ value: v, label: v }));
  }, [deliveries, shipmentSel, unitSel, groupSel, sizeSel, search]);

  const sizeOptions = useMemo(() => {
    const byKey = new Map<string, string>();
    const s = (search || '').trim().toLowerCase();
    const phraseMatches = Array.from(s.matchAll(/"([^\"]+)"/g)).map(m => m[1].trim()).filter(Boolean);
    const remaining = s.replace(/"[^\"]+"/g, ' ').trim();
    const tokens = remaining.split(/\s+/).filter(Boolean);
    (deliveries || [])
      .filter(rec => {
        if (!s) return true;
        const hay = [
          String(rec.mirr_no || ''),
          String(rec.invoice_no || ''),
          String(rec.package_no || ''),
          String(rec.ident_code || ''),
          rec.short_desc || '',
          rec.long_desc || '',
          rec.delivery_location || '',
        ].join(' ').toLowerCase();
        if (phraseMatches.length > 0 && !phraseMatches.every(p => hay.includes(p))) return false;
        if (tokens.length > 0 && !tokens.every(t => hay.includes(t))) return false;
        return true;
      })
      .filter(r => !shipmentSel || String(r.shipment_id || '') === shipmentSel)
      .filter(r => !unitSel || norm(r.unit).toLowerCase() === norm(unitSel).toLowerCase())
      .filter(r => !groupSel || norm(r.material_group).toLowerCase() === norm(groupSel).toLowerCase())
      .filter(r => !statusSel || norm(r.delivery_status).toLowerCase() === norm(statusSel).toLowerCase())
      .forEach(r => {
        const raw = norm(r.size_text);
        const trimmed = raw.trim();
        if (!trimmed) return;
        const k = trimmed.toLowerCase();
        if (!byKey.has(k)) byKey.set(k, trimmed);
      });
    return Array.from(byKey.values()).sort((a,b)=>a.localeCompare(b)).map(v => ({ value: v, label: v }));
  }, [deliveries, shipmentSel, unitSel, groupSel, statusSel, search]);

  const filteredData = useMemo(() => {
    const [start, end] = dateRange;
    const s = search.trim().toLowerCase();
    return (deliveries || []).filter((rec) => {
      if (s) {
        const hay = [
          String(rec.mirr_no || ''),
          // Exclude shipment_id (has dedicated filter)
          String(rec.invoice_no || ''),
          String(rec.package_no || ''),
          String(rec.ident_code || ''),
          rec.short_desc || '',
          rec.long_desc || '',
          // Exclude size (has dedicated filter)
          // Exclude unit (has dedicated filter)
          // Exclude material_group (has dedicated filter)
          rec.delivery_location || '',
          // Exclude delivery_status (has dedicated filter)
        ].join(' ').toLowerCase();

        // Multi-word AND + quoted phrases
        const phraseMatches = Array.from(s.matchAll(/"([^\"]+)"/g)).map(m => m[1].trim()).filter(Boolean);
        const remaining = s.replace(/"[^\"]+"/g, ' ').trim();
        const tokens = remaining.split(/\s+/).filter(Boolean);

        if (phraseMatches.length > 0 && !phraseMatches.every(p => hay.includes(p))) return false;
        if (tokens.length > 0 && !tokens.every(t => hay.includes(t))) return false;
      }
      if (shipmentSel && String(rec.shipment_id || '') !== shipmentSel) return false;
      if (unitSel && norm(rec.unit).toLowerCase() !== norm(unitSel).toLowerCase()) return false;
      if (groupSel && norm(rec.material_group).toLowerCase() !== norm(groupSel).toLowerCase()) return false;
      if (statusSel && norm(rec.delivery_status).toLowerCase() !== norm(statusSel).toLowerCase()) return false;
      if (sizeSel && norm(rec.size_text).toLowerCase() !== norm(sizeSel).toLowerCase()) return false;
      if (start || end) {
        const d = rec.delivery_date ? new Date(rec.delivery_date) : null;
        if (!d) return false;
        if (start) {
          const ds = new Date(start);
          if (d < new Date(ds.getFullYear(), ds.getMonth(), ds.getDate())) return false;
        }
        if (end) {
          const de = new Date(end);
          if (d > new Date(de.getFullYear(), de.getMonth(), de.getDate(), 23, 59, 59, 999)) return false;
        }
      }
      return true;
    });
  }, [deliveries, search, dateRange, shipmentSel, unitSel, groupSel, statusSel, sizeSel]);

  // Auto-clear invalid selections when options narrow
  useEffect(() => {
    if (shipmentSel && !shipmentOptions.some(o => (o.value as string) === shipmentSel)) setShipmentSel('');
  }, [shipmentOptions, shipmentSel]);
  useEffect(() => {
    if (unitSel && !unitOptions.some(o => (o.value as string) === unitSel)) setUnitSel('');
  }, [unitOptions, unitSel]);
  useEffect(() => {
    if (groupSel && !groupOptions.some(o => (o.value as string) === groupSel)) setGroupSel('');
  }, [groupOptions, groupSel]);
  useEffect(() => {
    if (statusSel && !statusOptions.some(o => (o.value as string) === statusSel)) setStatusSel('');
  }, [statusOptions, statusSel]);
  useEffect(() => {
    if (sizeSel && !sizeOptions.some(o => (o.value as string) === sizeSel)) setSizeSel('');
  }, [sizeOptions, sizeSel]);

  const columns: ColumnsType<MatClntDelivery> = useMemo(() => [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80, sorter: (a, b) => (a.id ?? 0) - (b.id ?? 0) },
    { title: 'MIRR No', dataIndex: 'mirr_no', key: 'mirr_no', width: 140, sorter: (a, b) => (a.mirr_no || '').localeCompare(b.mirr_no || '') },
    { title: 'Shipment#', dataIndex: 'shipment_id', key: 'shipment_id', width: 100, sorter: (a, b) => (String(a.shipment_id || '')).localeCompare(String(b.shipment_id || '')) },
    { title: 'Invoice#', dataIndex: 'invoice_no', key: 'invoice_no', width: 100, sorter: (a, b) => (String(a.invoice_no || '')).localeCompare(String(b.invoice_no || '')) },
    { title: 'Package#', dataIndex: 'package_no', key: 'package_no', width: 100, sorter: (a, b) => (String(a.package_no || '')).localeCompare(String(b.package_no || '')) },
    { title: 'Ident Code', dataIndex: 'ident_code', key: 'ident_code', width: 140, sorter: (a, b) => (a.ident_code || '').localeCompare(b.ident_code || '') },
    { title: 'Short Desc', dataIndex: 'short_desc', key: 'short_desc', ellipsis: true, width: 300, sorter: (a, b) => (a.short_desc || '').localeCompare(b.short_desc || ''),
      render: (_: any, rec) => rec.short_desc || '-' },
    { title: 'Long Desc', dataIndex: 'long_desc', key: 'long_desc', ellipsis: true, width: 200, sorter: (a, b) => (a.long_desc || '').localeCompare(b.long_desc || '') },
    { title: 'Size', dataIndex: 'size_text', key: 'size_text', width: 80, sorter: (a, b) => (a.size_text || '').localeCompare(b.size_text || ''),
      render: (_: any, rec) => rec.size_text || '-' },
    { title: 'Unit', dataIndex: 'unit', key: 'unit', width: 90, sorter: (a, b) => (a.unit || '').localeCompare(b.unit || '') },
    { title: 'Group', dataIndex: 'material_group', key: 'material_group', width: 100, sorter: (a, b) => (a.material_group || '').localeCompare(b.material_group || '') },
    { title: 'Qty', dataIndex: 'delivery_qty', key: 'delivery_qty', width: 120, align: 'right', render: (v: unknown) => formatNumberSmart(v), sorter: (a, b) => (a.delivery_qty ?? 0) - (b.delivery_qty ?? 0) },
    { title: 'Delivery Date', dataIndex: 'delivery_date', key: 'delivery_date', width: 120,
      render: (v: string) => v ? dayjs(v).format('YYYY-MM-DD') : '-',
      sorter: (a, b) => new Date(a.delivery_date || 0).getTime() - new Date(b.delivery_date || 0).getTime(),
      exportValue: (r: MatClntDelivery) => r.delivery_date ? dayjs(r.delivery_date).format('YYYY-MM-DD') : ''
    },
    { title: 'Location', dataIndex: 'delivery_location', key: 'delivery_location', width: 180, ellipsis: true, sorter: (a, b) => (a.delivery_location || '').localeCompare(b.delivery_location || '') },
    { title: 'Status', dataIndex: 'delivery_status', key: 'delivery_status', width: 100, sorter: (a, b) => (a.delivery_status || '').localeCompare(b.delivery_status || '') },
  ], []);

  return (
    <Card bordered={false} bodyStyle={{ padding: 8 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>Client Deliveries</Title>
        </Col>
      </Row>
      <Row  justify="space-between" align="middle" style={{ marginBottom: 8 }}>
        <Col>
          <Space size={8}>
            <Select
              placeholder="Shipment ID"
              allowClear
              showSearch
              size="small"
              value={shipmentSel || undefined}
              onChange={(v) => setShipmentSel(v || '')}
              options={shipmentOptions}
              style={{ width: 140 }}
              filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())}
            />
            <Select
              placeholder="Unit"
              allowClear
              showSearch
              size="small"
              value={unitSel || undefined}
              onChange={(v) => setUnitSel(v || '')}
              options={unitOptions}
              style={{ width: 70 }}
              filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())}
            />
            <Select
              placeholder="Group"
              allowClear
              showSearch
              size="small"
              value={groupSel || undefined}
              onChange={(v) => setGroupSel(v || '')}
              options={groupOptions}
              style={{ width: 80 }}
              filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())}
            />
            <Select
              placeholder="Size"
              allowClear
              showSearch
              size="small"
              value={sizeSel || undefined}
              onChange={(v) => setSizeSel(v || '')}
              options={sizeOptions}
              style={{ width: 120 }}
              filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())}
            />
            <Select
              placeholder="Status"
              allowClear
              showSearch
              size="small"
              value={statusSel || undefined}
              onChange={(v) => setStatusSel(v || '')}
              options={statusOptions}
              style={{ width: 70 }}
              filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())}
            />
            <Input
              placeholder="Search MIRR, ident, desc, invoice, package..."
              allowClear
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: 240 }}
            />
            <RangePicker
              size="small"
              style={{ width: 200 }}
              value={[
                dateRange[0] ? dayjs(dateRange[0]) : null,
                dateRange[1] ? dayjs(dateRange[1]) : null,
              ]}
              onChange={(vals) => {
                if (!vals || vals.length !== 2) { setDateRange([null, null]); return; }
                setDateRange([
                  vals[0] ? vals[0].format('YYYY-MM-DD') : null,
                  vals[1] ? vals[1].format('YYYY-MM-DD') : null,
                ]);
              }}
            />
            <Button
              size="small"
              onClick={() => {
                setShipmentSel('');
                setUnitSel('');
                setGroupSel('');
                setSizeSel('');
                setStatusSel('');
                setSearch('');
                setDateRange([null, null]);
              }}
            >
              Reset Filters
            </Button>
          </Space>
        </Col>
      </Row>
      <StandardTable<MatClntDelivery>
        columns={columns}
        dataSource={filteredData}
        rowKey={(r) => String(r.id)}
        loading={isLoading}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        enableExport
        scroll={{ x: 'max-content' }}
      />
    </Card>
  );
}
